// ─── SSE Event Types ──────────────────────────────────────────────────────────

export type SSEEventType =
  | 'decision.created'
  | 'decision.updated'
  | 'activity.logged'
  | 'health.tick'
  | 'connected'
  | 'briefing.started'
  | 'briefing.complete'
  | 'briefing.section.1.loading'
  | 'briefing.section.1.populated'
  | 'briefing.section.2.loading'
  | 'briefing.section.2.populated'
  | 'briefing.section.3.loading'
  | 'briefing.section.3.populated'
  | 'briefing.section.4.loading'
  | 'briefing.section.4.populated'
  | 'briefing.section.5.loading'
  | 'briefing.section.5.populated'
  | 'briefing.section.6.loading'
  | 'briefing.section.6.populated'
  | 'briefing.section.7.loading'
  | 'briefing.section.7.populated'
  | 'briefing.section.8.loading'
  | 'briefing.section.8.populated'
  | 'briefing.section.9.loading'
  | 'briefing.section.9.populated'
  // P2-6: these four were declared here under names no emitter ever broadcasts
  // (midday.update / eod.update / decisions.overload / decisions.slow-response).
  // The runtime emitters are canonical — see src/executors/midday-checkin.executor.ts,
  // src/executors/eod-summary.executor.ts, and src/monitoring/overload-detector.ts.
  | 'briefing.midday'
  | 'briefing.eod'
  | 'system.overload-critical'
  | 'system.overload-warning'
  | 'compliance.updated'
  | 'compliance.critical'
  | 'finance.mrr_updated'
  | 'finance.widget_update'
  | 'finance.payment_failed'
  | 'market.scan.complete'
  | 'market.intel.updated'
  | 'widget:update'

export interface SSEEvent<T = unknown> {
  type: SSEEventType
  data: T
  id?: string
  timestamp: string
}

export type SSEConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'error' | 'closed'

// ─── SSE Client ───────────────────────────────────────────────────────────────

export type SSEHandler<T = unknown> = (event: SSEEvent<T>) => void

export class SSEClient {
  private eventSource: EventSource | null = null
  private handlers = new Map<SSEEventType | '*', Set<SSEHandler>>()
  private statusListeners = new Set<(status: SSEConnectionStatus) => void>()
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 10
  private baseReconnectDelay = 1000
  private _status: SSEConnectionStatus = 'connecting'
  private url: string
  private withCredentials: boolean
  private closed = false

  constructor(url: string, options: { withCredentials?: boolean } = {}) {
    this.url = url
    // The runtime serves the dashboard same-origin and gates /api/v1/stream
    // behind Clerk. EventSource cannot send an Authorization header, so it
    // relies on the Clerk `__session` cookie being sent with the request —
    // which requires withCredentials (and matters if VITE_API_URL ever points
    // the dashboard at a different origin).
    this.withCredentials = options.withCredentials ?? true
    this.connect()
  }

  private connect() {
    this.setStatus('connecting')
    this.eventSource = new EventSource(this.url, { withCredentials: this.withCredentials })

    this.eventSource.onopen = () => {
      this.reconnectAttempts = 0
      this.setStatus('connected')
    }

    this.eventSource.onerror = () => {
      if (this.closed) return
      this.setStatus('reconnecting')
      this.eventSource?.close()
      this.scheduleReconnect()
    }

    // Listen to all named events we care about
    const namedEvents: SSEEventType[] = [
      'decision.created',
      'decision.updated',
      'activity.logged',
      'health.tick',
      'connected',
      'briefing.started',
      'briefing.complete',
      'briefing.section.1.loading',
      'briefing.section.1.populated',
      'briefing.section.2.loading',
      'briefing.section.2.populated',
      'briefing.section.3.loading',
      'briefing.section.3.populated',
      'briefing.section.4.loading',
      'briefing.section.4.populated',
      'briefing.section.5.loading',
      'briefing.section.5.populated',
      'briefing.section.6.loading',
      'briefing.section.6.populated',
      'briefing.section.7.loading',
      'briefing.section.7.populated',
      'briefing.section.8.loading',
      'briefing.section.8.populated',
      'briefing.section.9.loading',
      'briefing.section.9.populated',
      'finance.payment_failed',
      'briefing.midday',
      'briefing.eod',
      'system.overload-critical',
      'system.overload-warning',
      'compliance.updated',
      'compliance.critical',
      'finance.mrr_updated',
      'finance.widget_update',
      'market.scan.complete',
      'market.intel.updated',
      'widget:update',
    ]

    namedEvents.forEach((eventType) => {
      this.eventSource?.addEventListener(eventType, (e: MessageEvent) => {
        try {
          const parsed = JSON.parse(e.data) as SSEEvent
          this.dispatch({ ...parsed, type: eventType })
        } catch {
          // ignore malformed events
        }
      })
    })

    // Fallback: unnamed message events
    this.eventSource.onmessage = (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data) as SSEEvent
        this.dispatch(parsed)
      } catch {
        // ignore
      }
    }
  }

  private scheduleReconnect() {
    if (this.closed || this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.setStatus('error')
      return
    }
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(1.5, this.reconnectAttempts),
      30_000
    )
    this.reconnectAttempts++
    this.reconnectTimer = setTimeout(() => this.connect(), delay)
  }

  private dispatch(event: SSEEvent) {
    const typeHandlers = this.handlers.get(event.type)
    typeHandlers?.forEach((h) => h(event))
    const wildcardHandlers = this.handlers.get('*')
    wildcardHandlers?.forEach((h) => h(event))
  }

  private setStatus(status: SSEConnectionStatus) {
    this._status = status
    this.statusListeners.forEach((l) => l(status))
  }

  get status(): SSEConnectionStatus {
    return this._status
  }

  on<T>(type: SSEEventType | '*', handler: SSEHandler<T>) {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set())
    this.handlers.get(type)!.add(handler as SSEHandler)
    return () => this.off(type, handler as SSEHandler)
  }

  off(type: SSEEventType | '*', handler: SSEHandler) {
    this.handlers.get(type)?.delete(handler)
  }

  onStatusChange(listener: (status: SSEConnectionStatus) => void) {
    this.statusListeners.add(listener)
    return () => this.statusListeners.delete(listener)
  }

  close() {
    this.closed = true
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.eventSource?.close()
    this.setStatus('closed')
  }
}
